import { FC } from "react";
import { Link } from "react-router-dom";
import { Col, Row, Space, Typography } from "antd";
import NumberFormat from "react-number-format";
import { Supplier } from "../../../../types/SupplierTypes";
import { roundToTwoDecimals } from "../../../../common/Number";
import { SvgIcon } from "../../../../common/components/SvgIcon";
import { ScoreTag } from "../../../../common/components/ScoreTag";
import { CompanyImage } from "../../../../common/components/CompanyImage";
import { EmployeeCount } from "../../../../types/FinanceTypes";
import classes from "./VendorIntelligence.module.less";

export interface VendorIntelligenceProps extends Partial<Supplier> {
    contracts?: number;
    vendorKey?: string;
    employees?: EmployeeCount[];
}

export const VendorIntelligence: FC<VendorIntelligenceProps> = ({
    id,
    name = "",
    logo = "",
    address = "",
    esgScore = 0,
    employees = [],
    analysis,
    website,
    linkedIn,
    twitter,
    contracts = 0,
    vendorKey = "",
}) => {
    const home = website && (website.startsWith("http") ? website : `https://www.${website}`);
    const employeeCount = employees?.[0]?.employeeCount;
    const previousEmployeeCount = employees?.[1]?.employeeCount;
    const employeeChange =
        Number((employeeCount - previousEmployeeCount) / previousEmployeeCount) || 0;

    const employeeChangePercentage = roundToTwoDecimals(employeeChange * 100);

    const socialNetworks = [
        {
            url: home,
            testid: "supplier-profile-website",
            icon: <SvgIcon name="global" />,
        },
        {
            url: linkedIn,
            icon: <SvgIcon name="linkedin" />,
        },
        {
            url: twitter,
            icon: <SvgIcon name="x" />,
        },
    ];

    const scoreBlock = esgScore ? (
        <>
            <Space direction="vertical" size={8}>
                ESG score
                <ScoreTag score={esgScore} type="fraction" filled />
            </Space>
        </>
    ) : null;

    const employeeBlock = employeeCount ? (
        <Space direction="vertical" size={8}>
            Number of employees
            <Space className={classes.employee}>
                <NumberFormat
                    value={employeeCount}
                    className={classes.employeeNumber}
                    displayType="text"
                    thousandSeparator
                ></NumberFormat>
                {`${employeeChangePercentage}% ${employeeChangePercentage < 0 ? "Decrease" : "Increase"}`}
            </Space>
        </Space>
    ) : null;

    const leverageScoreBlock = analysis?.leverage.score ? (
        <Space direction="vertical" size={8}>
            Leverage score
            <ScoreTag type="fraction" score={analysis.leverage.score} denominator={10} filled />
        </Space>
    ) : null;

    const contractsBlock = contracts ? (
        <Space direction="vertical" size={8}>
            Contracts
            <span className={classes.contracts}>{contracts}</span>
        </Space>
    ) : null;

    const briefLink = id ? `/supplier/${id}` : "/supplier";

    return (
        <div className={classes.container}>
            <Row>
                <Col>
                    <CompanyImage logo={logo} name={name} size="large" sharp />
                </Col>
                <Col flex={1}>
                    <div className={classes.info}>
                        <div className={classes.nameWrapper}>
                            <Link to={`/vendor/${vendorKey}`}>
                                <div className={classes.name}>{name}</div>
                            </Link>
                            <Typography.Paragraph
                                ellipsis={{ tooltip: address, rows: 2 }}
                                className={classes.address}
                            >
                                {address}
                            </Typography.Paragraph>
                        </div>
                        {scoreBlock}
                        {employeeBlock}
                        {leverageScoreBlock}
                        {contractsBlock}
                    </div>
                </Col>
                <Col>
                    <div className={classes.social}>
                        {socialNetworks.map((item) => {
                            return item.url ? (
                                <a
                                    href={item.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={item.testid}
                                    key={item.testid}
                                >
                                    {item.icon}
                                </a>
                            ) : null;
                        })}
                        <Link to={briefLink}>
                            <span className={classes.brie}>
                                <SvgIcon name="briefs" />
                            </span>
                        </Link>
                    </div>
                </Col>
            </Row>
        </div>
    );
};
